import { crc32 } from "./wear-levelling.ts";

/**
 * A read-only reader of an ESP-IDF NVS partition: the entries an application
 * would find in it, after the checks and recovery steps the NVS library runs
 * when it loads the partition. The page's README describes the layout.
 */

export type NvsType =
    "u8" | "i8" | "u16" | "i16" | "u32" | "i32" | "u64" | "i64" | "float" | "double" | "string" | "blob";

export interface NvsEntry {
    namespace: string;
    key: string;
    type: NvsType;
    /** A bigint for the 64-bit integers; a string without its terminating NUL. */
    value: number | bigint | string | Uint8Array;
    /** Where the entry, or a blob's index, is: the page's position in the partition and the entry's in the page. */
    page: number;
    index: number;
}

export interface NvsPartition {
    /** In the order the library finds them: the oldest page first. */
    entries: NvsEntry[];
    /** 1, or 2 with blobs split into chunks across pages; that of the newest page. */
    version: 1 | 2;
    pages: { active: number; full: number; freeing: number; empty: number; corrupt: number };
    /** Written entries the library drops: damaged, half written, or a blob's chunks that do not add up. */
    skipped: number;
}

/** What `readNvs` throws for bytes that hold no NVS partition it can read. */
export class NotNvs extends Error {}

const PAGE = 4096;
const ENTRY = 32;
/** Entries in a page after its header and its entry state bitmap. */
const ENTRIES = 126;
const FIRST_ENTRY = 64;

const PAGE_EMPTY = 0xffffffff;
const PAGE_ACTIVE = 0xfffffffe;
const PAGE_FULL = 0xfffffffc;
const PAGE_FREEING = 0xfffffff8;
/** The format byte the library writes; it refuses a page with a lower one, a newer format. */
const VERSION_1 = 0xff;
const VERSION_2 = 0xfe;

const STATE_EMPTY = 0b11;
const STATE_WRITTEN = 0b10;

const NS_INDEX = 0;
const NS_ANY = 0xff;
const CHUNK_ANY = 0xff;
/** A blob's chunks are numbered from 0 or from 128, alternately as it is rewritten. */
const CHUNK_VERSION_1 = 0x80;

const SZ = 0x21;
const BLOB = 0x41;
const BLOB_DATA = 0x42;
const BLOB_INDEX = 0x48;
const FIXED: Record<number, NvsType> = {
    0x01: "u8",
    0x11: "i8",
    0x02: "u16",
    0x12: "i16",
    0x04: "u32",
    0x14: "i32",
    0x08: "u64",
    0x18: "i64",
    0x24: "float",
    0x28: "double",
};

/** One written entry that passed the library's checks, with a variable-length one's data. */
interface Item {
    ns: number;
    type: number;
    chunk: number;
    key: string;
    /** The 8 bytes after the key. */
    data: DataView;
    /** What the entries after a string's, a blob's or a chunk's hold. */
    payload: Uint8Array | undefined;
    page: number;
    index: number;
}

interface Page {
    position: number;
    state: number;
    sequence: number;
    version: number;
}

export function readNvs(bytes: Uint8Array): NvsPartition {
    if (bytes.length === 0 || bytes.length % PAGE !== 0) throw new NotNvs("the size is not a whole number of pages");
    if (bytes.every((byte) => byte === 0xff)) throw new NotNvs("the partition is erased");
    const counts = { active: 0, full: 0, freeing: 0, empty: 0, corrupt: 0 };
    let pages: Page[] = [];
    for (let position = 0; position * PAGE < bytes.length; position++) {
        const page = readHeader(bytes.subarray(position * PAGE, (position + 1) * PAGE), position);
        if (page === "empty") counts.empty++;
        else if (page === "corrupt") counts.corrupt++;
        else pages.push(page);
    }
    for (const page of pages) {
        if (page.version < VERSION_2) throw new NotNvs("a page is in a newer NVS format than this reader knows");
        if (page.state === PAGE_ACTIVE) counts.active++;
        else if (page.state === PAGE_FULL) counts.full++;
        else counts.freeing++;
    }
    if (pages.length === 0) throw new NotNvs("no page has a valid NVS header");
    pages.sort((a, b) => a.sequence - b.sequence);
    // Power lost while a page was being freed: the library copies the page again into a new one.
    const last = pages.at(-1)!;
    if (pages.some((page) => page.state === PAGE_FREEING) && last.state === PAGE_ACTIVE) pages = pages.slice(0, -1);

    let written = 0;
    let skipped = 0;
    // A later item replaces an earlier one with the same namespace, key and chunk, as a rewrite does.
    const items = new Map<string, Item>();
    for (const page of pages) {
        const result = readItems(bytes.subarray(page.position * PAGE, (page.position + 1) * PAGE), page);
        written += result.written;
        skipped += result.skipped;
        for (const item of result.items) {
            const id = `${item.ns}/${item.key}/${item.chunk}`;
            items.delete(id);
            items.set(id, item);
        }
    }
    if (written > 0 && items.size === 0) {
        throw new NotNvs("no entry reads as plain NVS: the partition is encrypted, or damaged");
    }

    const namespaces = new Map<number, string>();
    for (const item of items.values()) {
        if (item.ns === NS_INDEX && item.type === 0x01) namespaces.set(item.data.getUint8(0), item.key);
    }
    const entries: NvsEntry[] = [];
    for (const item of items.values()) {
        if (item.ns === NS_INDEX || item.ns === NS_ANY || item.type === BLOB_DATA) continue;
        const entry = (type: NvsType, value: NvsEntry["value"]): NvsEntry => ({
            namespace: namespaces.get(item.ns) ?? `#${item.ns}`,
            key: item.key,
            type,
            value,
            page: item.page,
            index: item.index,
        });
        if (item.type === BLOB_INDEX) {
            const blob = joinChunks(item, items);
            if (blob === undefined) skipped++;
            else entries.push(entry("blob", blob));
        } else if (item.type === SZ) {
            const text = item.payload!;
            entries.push(entry("string", new TextDecoder().decode(text.at(-1) === 0 ? text.subarray(0, -1) : text)));
        } else if (item.type === BLOB) {
            entries.push(entry("blob", item.payload!));
        } else {
            entries.push(entry(FIXED[item.type]!, fixedValue(item)));
        }
    }
    return { entries, version: pages.at(-1)!.version === VERSION_1 ? 1 : 2, pages: counts, skipped };
}

function readHeader(page: Uint8Array, position: number): Page | "empty" | "corrupt" {
    const view = new DataView(page.buffer, page.byteOffset, page.byteLength);
    const state = view.getUint32(0, true);
    if (state === PAGE_EMPTY) return page.every((byte) => byte === 0xff) ? "empty" : "corrupt";
    if (crc32(page.subarray(4, 28)) !== view.getUint32(28, true)) return "corrupt";
    if (state !== PAGE_ACTIVE && state !== PAGE_FULL && state !== PAGE_FREEING) return "corrupt";
    return { position, state, sequence: view.getUint32(4, true), version: page[8]! };
}

/**
 * The page's written entries that pass `Item::checkHeaderConsistency`, whose
 * spans are written, and whose data matches its CRC. An active page is read
 * up to its first empty entry, where the library writes next.
 */
function readItems(bytes: Uint8Array, page: Page): { items: Item[]; written: number; skipped: number } {
    const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
    const state = (i: number) => (bytes[ENTRY + (i >> 2)]! >> ((i & 3) * 2)) & 3;
    const items: Item[] = [];
    let written = 0;
    let skipped = 0;
    for (let i = 0; i < ENTRIES; i++) {
        const entryState = state(i);
        if (entryState === STATE_EMPTY && page.state === PAGE_ACTIVE) break;
        if (entryState !== STATE_WRITTEN) continue;
        written++;
        const at = FIRST_ENTRY + i * ENTRY;
        const raw = bytes.subarray(at, at + ENTRY);
        const span = raw[2]!;
        if (!consistent(raw, view, at, i)) {
            skipped++;
            continue;
        }
        const keyBytes = raw.subarray(8, 24);
        const end = keyBytes.indexOf(0);
        const item: Item = {
            ns: raw[0]!,
            type: raw[1]!,
            chunk: raw[3]!,
            key: new TextDecoder().decode(keyBytes.subarray(0, end < 0 ? 15 : Math.min(end, 15))),
            data: new DataView(bytes.buffer, bytes.byteOffset + at + 24, 8),
            payload: undefined,
            page: page.position,
            index: i,
        };
        if (variableLength(item.type)) {
            i += span - 1;
            const spanWritten = Array.from({ length: span - 1 }, (_, j) => state(i - j)).every(
                (s) => s === STATE_WRITTEN,
            );
            const payload = bytes.subarray(at + ENTRY, at + ENTRY + item.data.getUint16(0, true));
            if (!spanWritten || crc32(payload) !== item.data.getUint32(4, true)) {
                skipped++;
                continue;
            }
            item.payload = payload;
        }
        items.push(item);
    }
    return { items, written, skipped };
}

function variableLength(type: number): boolean {
    return type === SZ || type === BLOB || type === BLOB_DATA;
}

/** `Item::checkHeaderConsistency` for the entry at `index`. */
function consistent(raw: Uint8Array, view: DataView, at: number, index: number): boolean {
    const crc = crc32(Uint8Array.from([...raw.subarray(0, 4), ...raw.subarray(8, 32)]));
    if (crc !== view.getUint32(at + 4, true)) return false;
    const [, type, span, chunk] = raw as unknown as [number, number, number, number];
    if (FIXED[type] !== undefined) return span === 1;
    if (type === BLOB_INDEX)
        return span === 1 && chunk === CHUNK_ANY && view.getUint32(at + 24, true) <= 127 * 125 * ENTRY;
    if (!variableLength(type)) return false;
    if (type === BLOB_DATA && chunk === CHUNK_ANY) return false;
    const size = view.getUint16(at + 24, true);
    return size <= (ENTRIES - index - 1) * ENTRY && span <= ENTRIES - index && span === Math.ceil(size / ENTRY) + 1;
}

/**
 * A version 2 blob from its index's chunks, or undefined when the chunks of
 * its version do not add up to the index's size and count, which the library
 * takes as a write cut short and erases.
 */
function joinChunks(index: Item, items: Map<string, Item>): Uint8Array | undefined {
    const size = index.data.getUint32(0, true);
    const count = index.data.getUint8(4);
    const start = index.data.getUint8(5);
    const family = [...items.values()].filter(
        (item) =>
            item.type === BLOB_DATA &&
            item.ns === index.ns &&
            item.key === index.key &&
            item.chunk >= start &&
            item.chunk < (start === 0 ? CHUNK_VERSION_1 : CHUNK_ANY),
    );
    const chunks = family.filter((item) => item.chunk < start + count).sort((a, b) => a.chunk - b.chunk);
    const observed = family.reduce((sum, item) => sum + item.payload!.length, 0);
    if (family.length !== count || chunks.length !== count || observed !== size) return undefined;
    const blob = new Uint8Array(size);
    let at = 0;
    for (const chunk of chunks) {
        blob.set(chunk.payload!, at);
        at += chunk.payload!.length;
    }
    return blob;
}

function fixedValue(item: Item): number | bigint {
    const data = item.data;
    switch (FIXED[item.type]) {
        case "u8":
            return data.getUint8(0);
        case "i8":
            return data.getInt8(0);
        case "u16":
            return data.getUint16(0, true);
        case "i16":
            return data.getInt16(0, true);
        case "u32":
            return data.getUint32(0, true);
        case "i32":
            return data.getInt32(0, true);
        case "u64":
            return data.getBigUint64(0, true);
        case "i64":
            return data.getBigInt64(0, true);
        case "float":
            return data.getFloat32(0, true);
        default:
            return data.getFloat64(0, true);
    }
}
